import React, { useState, useEffect, useRef } from "react";
import { ChevronDown, Check, Search } from "lucide-react";

export default function LiveSelect({
  value,
  label,
  options,
  onChange,
  isFont = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [originalValue, setOriginalValue] = useState(value);
  const [query, setQuery] = useState("");
  const containerRef = useRef(null);
  const searchRef = useRef(null);
  const enableSearch = isFont && options.length > 10;
  const visibleOptions =
    enableSearch && query
      ? options.filter((o) =>
          String(o.label || o.value)
            .toLowerCase()
            .includes(query.trim().toLowerCase()),
        )
      : options;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        if (isOpen) {
          onChange(originalValue);
          setIsOpen(false);
        }
      }
    };

    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === "Escape") {
        onChange(originalValue);
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, originalValue, onChange]);

  const handleOpen = () => {
    setOriginalValue(value);
    setQuery("");
    setIsOpen(true);
    if (enableSearch) {
      setTimeout(() => searchRef.current?.focus(), 0);
    }
  };

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setOriginalValue(optionValue);
    setQuery("");
    setIsOpen(false);
  };

  const handleHover = (optionValue) => {
    onChange(optionValue);
  };

  const selectedOption =
    options.find((opt) => opt.value === value) ||
    options.find((opt) => opt.value === originalValue) ||
    options[0];

  return (
    <div className="relative w-full" ref={containerRef}>
      <label className="ctrl">
        {label && <span className="mb-0.5">{label}</span>}
        <button
          type="button"
          onClick={() => (isOpen ? setIsOpen(false) : handleOpen())}
          className={`w-full flex items-center justify-between bg-white border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 transition-all hover:bg-gray-50 text-left font-medium ${
            isFont
              ? "px-4 py-3 min-h-[44px] text-[1.05rem]"
              : "px-3 py-[10px] text-[0.95rem]"
          }`}
        >
          <span
            className="truncate"
            style={isFont ? { fontFamily: value, fontWeight: 600 } : {}}
          >
            {selectedOption?.label || value}
          </span>
          <ChevronDown
            size={18}
            className={`text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </button>
      </label>

      {isOpen && (
        <div className="absolute z-[100] left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
          {enableSearch && (
            <div className="sticky top-0 bg-white border-b border-gray-100 p-2.5">
              <div className="relative rounded-lg bg-indigo-50/70 p-1">
                <Search
                  size={18}
                  className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400"
                />
                <input
                  ref={searchRef}
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search fonts..."
                  className="w-full pl-9 pr-3 py-2 text-[1rem] bg-white border border-gray-200 rounded-md outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 placeholder:text-gray-400"
                />
              </div>
            </div>
          )}
          <div className="max-h-72 overflow-y-auto py-1 custom-scrollbar">
            {visibleOptions.map((option) => (
              <div
                key={option.value}
                className={`px-4 py-3 cursor-pointer flex items-center justify-between transition-all duration-200 group ${
                  value === option.value
                    ? "bg-indigo-50 text-indigo-700 font-semibold"
                    : "text-gray-700 hover:bg-gray-50 hover:text-indigo-600"
                }`}
                onMouseEnter={() => handleHover(option.value)}
                onClick={() => handleSelect(option.value)}
              >
                <span
                  style={isFont ? { fontFamily: option.value } : {}}
                  className={
                    isFont
                      ? "text-[1.05rem] leading-6 transition-transform duration-200 group-hover:translate-x-0.5 origin-left truncate"
                      : "text-[0.95rem] truncate"
                  }
                >
                  {option.label}
                </span>
                {value === option.value && (
                  <Check size={16} className="text-indigo-600 flex-shrink-0" />
                )}
              </div>
            ))}
            {enableSearch && query && visibleOptions.length === 0 && (
              <div className="px-3 py-4 text-center text-sm text-gray-400">
                No fonts match "{query}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
